import React, { useState } from 'react'
import { DataGridPro, gridClasses, GridColDef } from '@mui/x-data-grid-pro'
import { getMuiDataGridLocaleText } from '../../../i18n/localeUtils'
import { useTranslation } from 'react-i18next'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    useTheme,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'

import { FormProvider, useForm } from 'react-hook-form'
import { FactsTable, FactsTableRow } from '../../components/FactsTable'
import { RegisteredTextField } from '../../../components/fields/TextField'
import { RegisteredCheckBox } from '../../../components/fields/CheckBox'
import { PRFilterKeyword } from 'types/PRFilterKeyword'

type KeywordsGridProps = {
    keywords: PRFilterKeyword[]
    onUpdate: (keyword: Omit<PRFilterKeyword, 'createdAt'>) => Promise<void>
    onDelete: (id: string) => Promise<void>
}

type KeywordFormValues = {
    id: string
    language: 'sv' | 'en'
    phrase: string
    negation: number
}

const keywordsColumns = (
    handleOpenDialog: (keyword: PRFilterKeyword) => void,
    onDelete: (id: string) => Promise<void>,
): GridColDef<PRFilterKeyword>[] => [
    {
        field: 'language',
        headerName: 'Språk',
        maxWidth: 100,
        flex: 1,
    },
    {
        field: 'field',
        headerName: 'Fält',
        maxWidth: 100,
        flex: 1,
    },
    {
        field: 'phrase',
        headerName: 'Fras',
        minWidth: 150,
        flex: 1,
    },
    {
        field: 'negation',
        headerName: 'Negation',
        minWidth: 150,
        flex: 1,
        valueFormatter: (value: number) => !!value,
    },
    {
        field: 'actions',
        type: 'actions',
        width: 80,
        renderCell: (params) => (
            <Stack direction='row'>
                <IconButton
                    onClick={() => {
                        handleOpenDialog(params.row)
                    }}
                >
                    <EditIcon color='warning' />
                </IconButton>
                <IconButton
                    onClick={() => {
                        void onDelete(params.row.id)
                    }}
                >
                    <DeleteIcon color='error' />
                </IconButton>
            </Stack>
        ),
    },
]

function KeywordsGrid({ keywords, onUpdate, onDelete }: KeywordsGridProps) {
    const [isOpen, setIsOpen] = useState(false)
    const theme = useTheme()
    const formMethods = useForm()
    const { i18n } = useTranslation()
    const locale = i18n.language

    const disableSelectedSellOutlineStyle = {
        [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
            outline: 'none',
        },
        [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
            outline: 'none',
        },
    }

    const handleCloseDialog = () => {
        setIsOpen(false)
        formMethods.reset()
    }

    const handleOpenDialog = (keyword: PRFilterKeyword) => {
        formMethods.reset({
            id: keyword.id,
            language: keyword.language,
            phrase: keyword.phrase,
            negation: keyword.negation,
        })
        setIsOpen(true)
    }

    const handleUpdateKeyword = async () => {
        const formData = formMethods.getValues() as KeywordFormValues
        await onUpdate({ ...formData, field: 'title' })
        formMethods.reset()
        setIsOpen(false)
    }

    return (
        <div style={{ width: '100%' }}>
            <Dialog open={isOpen} onClose={handleCloseDialog}>
                <DialogTitle>Uppdatera keyword</DialogTitle>
                <DialogContent sx={{ paddingBottom: 0 }}>
                    <DialogContentText>
                        To subscribe to this website, please enter your email address here. We will send updates
                        occasionally.
                    </DialogContentText>
                    <FormProvider {...formMethods}>
                        <FactsTable>
                            <FactsTableRow label='Språk'>
                                <FormControl fullWidth sx={{ mt: 2 }}>
                                    <InputLabel id='select-label'>Språk</InputLabel>
                                    <Select
                                        labelId='select-label'
                                        name='language'
                                        label='Språk'
                                        fullWidth
                                        defaultValue={formMethods.getValues('language') || ''}
                                        onChange={(e) => {
                                            const value = e.target.value
                                            formMethods.setValue('language', value as 'sv' | 'en')
                                        }}
                                    >
                                        <MenuItem value='' disabled>
                                            Välj språk
                                        </MenuItem>
                                        <MenuItem value='sv'>sv</MenuItem>
                                        <MenuItem value='en'>en</MenuItem>
                                    </Select>
                                </FormControl>
                            </FactsTableRow>
                            <FactsTableRow label='Fras'>
                                <RegisteredTextField label='Fras' name='phrase' fullWidth />
                            </FactsTableRow>
                            <FactsTableRow label='Negation'>
                                <RegisteredCheckBox label='True' name='negation' fullWidth />
                            </FactsTableRow>
                        </FactsTable>

                        <DialogActions>
                            <Button onClick={handleCloseDialog}>Stäng</Button>
                            <Button
                                onClick={() => {
                                    void handleUpdateKeyword()
                                }}
                                disabled={
                                    !['sv', 'en'].includes(formMethods.watch('language')) ||
                                    formMethods.watch('phrase').length === 0
                                }
                            >
                                Uppdatera
                            </Button>
                        </DialogActions>
                    </FormProvider>
                </DialogContent>
            </Dialog>
            <DataGridPro
                sx={{
                    ...disableSelectedSellOutlineStyle,
                    width: '100%',
                    '& .even': {
                        backgroundColor: theme.palette.rowColor.main, // Use palette color for even rows
                    },
                    '& .odd': {
                        // backgroundColor: theme.palette.rowColor.light, // Use palette color for odd rows
                    },
                }}
                density='compact'
                rows={keywords}
                columns={keywordsColumns(handleOpenDialog, onDelete)}
                localeText={getMuiDataGridLocaleText(locale)}
                getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
                disableColumnFilter // Disable column filter options
                disableColumnMenu // Disable column menu
                disableRowSelectionOnClick // Disable row selection on click
            />
        </div>
    )
}

export default KeywordsGrid
